// 빠진 항목 제안 — 실무에서 거의 늘 같이 들어가는 항목이 없을 때만 알린다(과잉 제안 금지).

import { evalQtyExpr } from "./qty-expr";
import { EditorItem, EditorState, EditorTrade } from "./editor/types";
import { TradeCode } from "../template/trades";

export type MissingRuleId =
  | "waste-disposal"
  | "bath-waterproof"
  | "wet-waterproof"
  | "protection"
  | "final-cleaning"
  | "wallpaper-prep"
  | "paint-prep";

export interface MissingSuggestion {
  id: MissingRuleId;
  message: string;
  trade_code: TradeCode;
  suggestion: {
    name: string;
    unit: string;
    qty?: number;
    qty_expr?: string;
  };
  severity: "high" | "low";
}

export type MissingRulesInput = Pick<EditorState, "trades" | "area_pyeong">;

interface Line {
  trade: EditorTrade;
  text: string;
}

// 공백을 지우고 소문자·NFC 로 맞춘다 — 엑셀에서 온 NFD 한글도 같은 글자로 본다.
const norm = (raw: string): string => raw.normalize("NFC").replace(/\s+/g, "").toLowerCase();

const hasAny = (text: string, words: readonly string[]): boolean => words.some((w) => text.includes(w));

const WASTE = ["폐기물", "폐자재", "반출", "마대", "컨테이너", "잔재"];
const WATERPROOF = ["방수", "고뫄스"];
const PROTECTION = ["보양"];
const CLEANING = ["청소", "클리닝"];
const WALLPAPER_PREP = ["퍼티", "빠데", "핸디", "초배", "면처리", "면정리", "바탕작업", "밑작업"];
const PAINT_PREP = ["퍼티", "빠데", "핸디", "면처리", "면정리", "젯소", "바탕작업", "밑작업", "프라이머"];
const BATH_WORDS = ["욕실", "화장실", "욕조", "샤워"];
const WET_WORDS = ["발코니", "베란다", "세탁실", "다용도실"];
// 발코니 세라믹·탄성코트는 면처리가 시공에 포함돼 따로 적지 않는다.
const PAINT_NO_PREP = ["세라믹", "탄성", "발코니", "베란다", "결로", "방수"];
const DUSTY_TRADES: readonly TradeCode[] = ["demolition", "carpentry"];
const MIN_TRADES_FOR_CLEANING = 3;
// 청소 제안의 공종 수에서 빼는 것 — 부대 공종(서류대행·보양)과 제안이 넣는 부대 항목 줄. 제안을 받을수록 다음 제안이 뜨면 안 된다.
const AUX_TRADES: readonly TradeCode[] = ["docs"];
const AUX_LINES = [...WASTE, ...WATERPROOF, ...PROTECTION, ...CLEANING];
// 기존 타일을 손보는 줄 — 방수층을 새로 깔지 않는다.
const TILE_REPAIR = ["덧방", "줄눈", "보수", "코킹"];

const isBlank = (item: EditorItem): boolean => item.item.trim() === "" && item.scope.trim() === "";

const linesOf = (trades: readonly EditorTrade[]): Line[] => {
  const out: Line[] = [];
  const push = (trade: EditorTrade, item: EditorItem): void => {
    if (!isBlank(item)) out.push({ trade, text: norm(`${item.scope} ${item.item}`) });
    item.children.forEach((child) => push(trade, child));
  };
  trades.forEach((trade) => trade.items.forEach((item) => push(trade, item)));
  return out;
};

const areaQty = (area: number | null): { qty?: number; qty_expr?: string } => {
  if (area === null) return {};
  const result = evalQtyExpr("평", { area_pyeong: area });
  return result.ok ? { qty: result.value, qty_expr: "평" } : {};
};

/** 견적에서 빠졌을 가능성이 큰 항목을 규칙 순서대로 돌려준다. `ignored` 의 id 는 뺀다. */
export const suggestMissingItems = (
  state: MissingRulesInput,
  ignored: readonly string[] = []
): MissingSuggestion[] => {
  const lines = linesOf(state.trades);
  if (lines.length === 0) return [];

  const activeTrades = new Set(lines.map((l) => l.trade.trade));
  const anyLine = (words: readonly string[]): boolean => lines.some((l) => hasAny(l.text, words));
  const inTrade = (code: TradeCode): Line[] => lines.filter((l) => l.trade.trade === code);
  const tileLines = lines.filter(
    (l) => l.text.includes("타일") && !l.text.includes("철거") && !hasAny(l.text, TILE_REPAIR)
  );
  const workTrades = new Set(
    lines
      .filter((l) => !AUX_TRADES.includes(l.trade.trade) && !hasAny(l.text, AUX_LINES))
      .map((l) => l.trade.trade)
  );

  const hasDemolition =
    activeTrades.has("demolition") ||
    lines.some((l) => l.trade.group === "BASIC" && l.text.includes("철거"));
  const hasDusty = hasDemolition || DUSTY_TRADES.some((code) => activeTrades.has(code));
  const hasBathTile = tileLines.some(
    (l) => l.trade.trade === "bath" || hasAny(l.text, BATH_WORDS)
  );
  const hasWetTile = tileLines.some((l) => hasAny(l.text, WET_WORDS));
  const paintNeedsPrep = inTrade("paint").some(
    (l) => !hasAny(l.text, PAINT_NO_PREP) && !hasAny(l.text, PAINT_PREP)
  );

  const out: MissingSuggestion[] = [];

  if (hasDemolition && !anyLine(WASTE)) {
    out.push({
      id: "waste-disposal",
      message: "철거가 있는데 폐기물 처리 항목이 없습니다",
      trade_code: "demolition",
      suggestion: { name: "폐기물 처리 [양중·수거·운반]", unit: "T" },
      severity: "high",
    });
  }

  if (!anyLine(WATERPROOF)) {
    if (hasBathTile) {
      out.push({
        id: "bath-waterproof",
        message: "욕실 타일이 있는데 방수 항목이 없습니다",
        trade_code: "plumbing",
        suggestion: { name: "욕실 방수 작업", unit: "실" },
        severity: "high",
      });
    } else if (hasWetTile) {
      out.push({
        id: "wet-waterproof",
        message: "발코니·세탁실 타일이 있는데 방수 항목이 없습니다",
        trade_code: "plumbing",
        suggestion: { name: "발코니 방수 작업", unit: "실" },
        severity: "low",
      });
    }
  }

  if (hasDusty && !anyLine(PROTECTION)) {
    out.push({
      id: "protection",
      message: "먼지 나는 공사가 있는데 보양 항목이 없습니다",
      trade_code: "docs",
      suggestion: { name: "엘리베이터·공용부 보양", unit: "SET", qty: 1 },
      severity: hasDemolition ? "high" : "low",
    });
  }

  if (workTrades.size >= MIN_TRADES_FOR_CLEANING && !anyLine(CLEANING)) {
    out.push({
      id: "final-cleaning",
      message: "준공(입주) 청소 항목이 없습니다",
      trade_code: "finish",
      suggestion: { name: "입주청소", unit: "PY", ...areaQty(state.area_pyeong) },
      severity: "low",
    });
  }

  if (activeTrades.has("wallpaper") && !anyLine(WALLPAPER_PREP)) {
    out.push({
      id: "wallpaper-prep",
      message: "도배가 있는데 면처리(핸디·초배) 항목이 없습니다",
      trade_code: "wallpaper",
      suggestion: { name: "핸디작업 (면처리)", unit: "PY", ...areaQty(state.area_pyeong) },
      severity: "low",
    });
  }

  if (paintNeedsPrep && !anyLine(PAINT_PREP)) {
    out.push({
      id: "paint-prep",
      message: "도장이 있는데 퍼티·면처리 항목이 없습니다",
      trade_code: "paint",
      suggestion: { name: "퍼티·면처리", unit: "PY", ...areaQty(state.area_pyeong) },
      severity: "low",
    });
  }

  const skip = new Set(ignored);
  return out.filter((s) => !skip.has(s.id));
};
